import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog.tsx';
import { Button } from './ui/button.tsx';
import { Input } from './ui/input.tsx';
import { Label } from './ui/label.tsx';
import {
  FORGOT_PASSWORD_SUCCESS_MESSAGE,
  requestForgotPassword,
} from '../services/forgotPasswordService';

const ForgotPasswordDialog = ({ open, onOpenChange, initialEmail = '' }) => {
  const [email, setEmail] = useState(initialEmail);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (open) {
      setEmail(initialEmail);
      setError('');
      setSuccessMessage('');
    }
  }, [open, initialEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = (email || '').trim();
    if (!trimmed.includes('@')) {
      setError('Enter a valid email address.');
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccessMessage('');
    try {
      const message = await requestForgotPassword(trimmed);
      setSuccessMessage(message || FORGOT_PASSWORD_SUCCESS_MESSAGE);
    } catch (err) {
      setError(err.message || 'Unable to send reset email. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reset your password</DialogTitle>
          <DialogDescription>
            Enter your account email. We will send a link to set a new password. The link expires
            in one hour.
          </DialogDescription>
        </DialogHeader>

        {successMessage ? (
          <p className="text-sm text-foreground">{successMessage}</p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="forgot-password-email">Email</Label>
              <Input
                id="forgot-password-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                disabled={submitting}
              />
            </div>
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Sending…
                  </>
                ) : (
                  'Send reset link'
                )}
              </Button>
            </DialogFooter>
          </form>
        )}

        {successMessage ? (
          <DialogFooter>
            <Button type="button" onClick={() => onOpenChange(false)}>
              Back to sign in
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordDialog;
